import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, mergeMap, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ProposalService } from '../../service/proposal.service';
import { ProposalActions } from './proposal.actions';
import { AdministrationService } from '../../service/administration.service';
import { ImportRowErrorDto, ImportRowWarningDto } from '../../../../graphql/generated';
import { MessageService } from 'primeng/api';


@Injectable()
export class LookupProposalEffects {

  loadProposal$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(
        ofType(ProposalActions.loadProposal),
        mergeMap(({ parameters, fields, processCoverageIds, year, ids, legalEntityIds }) =>
          rs.loadProposal(processCoverageIds, parameters, year, ids, legalEntityIds).pipe(
            mergeMap((lookups) => [
              ProposalActions.loadProposalSuccess({ lookups, fields })
              // ProposalActions.loadProposalIds({ parameters, fields, processCoverageIds, year, ids, legalEntityIds }),
            ]),
            catchError((error) => of(ProposalActions.loadProposalFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadProposalIds$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(
        ofType(ProposalActions.loadProposalIds),
        mergeMap(({ parameters, processCoverageIds, year, ids, legalEntityIds }) =>
          rs.loadProposalIds(processCoverageIds, parameters, year, ids, legalEntityIds).pipe(
            mergeMap((ids) => [
              ProposalActions.loadProposalIdsSuccess({ ids })
            ]),
            catchError((error) => of(ProposalActions.loadProposalIdsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadProposalFilters$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(

        ofType(ProposalActions.loadProposalFilters),
        mergeMap(({ legalEntityIds, proposalIds, year }) =>
          rs.getProposalFilters(legalEntityIds, proposalIds, year).pipe(
            map((filters) => {
              return ProposalActions.loadProposalFiltersSuccess({
                filters
              });
            }),
            catchError((error) => of(ProposalActions.loadProposalFiltersFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadProposalOptions$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(

        ofType(ProposalActions.loadProposalOptions),
        mergeMap(({ year }) =>
          rs.loadProposalOptions(year).pipe(
            map((options) => {
              return ProposalActions.loadProposalOptionsSuccess({
                options
              });
            }),
            catchError((error) => of(ProposalActions.loadProposalOptionsFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  loadAllLookupProposal$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(

        ofType(ProposalActions.loadProposalAll),
        mergeMap(({ parameters, fields, processCoverageIds, year, ids, legalEntityIds }) =>
          rs.loadProposal(processCoverageIds, parameters, year, ids, legalEntityIds).pipe(
            map((lookups) => {
              return ProposalActions.loadProposalAllSuccess({
                lookups,
                fields
              });
            }),
            catchError((error) => of(ProposalActions.loadProposalAllFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  createProposal$ = createEffect(
    (
      rs: ProposalService = inject(ProposalService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(ProposalActions.createProposal),
        concatMap(({ proposal, parameters, processCoverageIds, fields, year, ids, legalEntityIds }) =>
          rs.createProposal(proposal).pipe(
            mergeMap((prop) => {
              prop?.warnings?.forEach(w => {
                if (w) {
                  messageService.add({
                    severity: 'warn',
                    summary: w.field ?? 'Warning',
                    detail: w.message ?? '',
                    life: 5000
                  });
                }
              });
              return [
                ProposalActions.loadProposal({ parameters, processCoverageIds, fields, year, ids, legalEntityIds }),
                ProposalActions.loadProposalFilters({ legalEntityIds, proposalIds: ids, year }),
                ProposalActions.loadProposalOptions({ year })
              ];
            }),
            catchError((error) =>
              of(ProposalActions.createProposalFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  deleteProposal$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(
        ofType(ProposalActions.deleteProposal),
        concatMap(({ id, parameters, processCoverageIds, fields, year, ids, legalEntityIds }) =>
          rs.deleteProposal(id).pipe(
            map(() =>
              ProposalActions.loadProposal({ parameters, processCoverageIds, fields, year, ids, legalEntityIds })
            ),
            catchError((error) =>
              of(ProposalActions.deleteProposalFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  duplicateProposal$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(
        ofType(ProposalActions.duplicateProposal),
        concatMap(({ proposal, parameters, processCoverageIds, fields, year, ids, legalEntityIds }) =>
          rs.createProposal(proposal).pipe(
            map(() =>
              ProposalActions.loadProposal({ parameters, processCoverageIds, fields, year, ids, legalEntityIds })
            ),
            catchError((error) =>
              of(ProposalActions.duplicateProposalFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  updateProposal$ = createEffect(
    (
      rs: ProposalService = inject(ProposalService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(ProposalActions.updateProposal),
        concatMap(({ proposal, parameters, processCoverageIds, fields, year, legalEntityIds, ids }) =>
          rs.updateProposal(proposal).pipe(
            mergeMap((prop) => {
              prop?.warnings?.forEach(w => {
                if (w) {
                  messageService.add({
                    severity: 'warn',
                    summary: w.field ?? 'Warning',
                    detail: w.message ?? '',
                    life: 5000
                  });
                }
              });
              return [
                ProposalActions.updateProposalSuccess({ proposal: prop }),
                ProposalActions.loadProposalFilters({ legalEntityIds, proposalIds: ids, year }),
                ProposalActions.loadProposalOptions({ year })
              ];
            }),
            catchError((error) =>
              of(ProposalActions.updateProposalFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  createAuditPan$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(
        ofType(ProposalActions.createAuditPlan),
        concatMap(({ date, legalEntityId, name, year }) =>
          rs.createAuditPlan(name, legalEntityId, date, year).pipe(
            map((auditPlan) =>
              // ProposalActions.createAuditPlanSuccess({ auditPlan })
              ProposalActions.loadProposalOptions({ year })
            ),
            catchError((error) =>
              of(ProposalActions.createAuditPlanFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  exportProposals$ = createEffect(
    (usService: ProposalService = inject(ProposalService), as: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(ProposalActions.downloadProposals),
        switchMap(({ parameters, processCoverageIds, year, fields, timeZone }) =>
          usService.downloadProposals(processCoverageIds, parameters, year, timeZone).pipe(
            map((exportProposals) => {
              as.downloadFile(
                exportProposals!.data![0]!.content,
                exportProposals!.data![0]!.filename!
              );
              return ProposalActions.downloadProposalsSuccess({
                proposals: exportProposals
              });
            }),
            catchError((error) =>
              of(ProposalActions.downloadProposalsFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  changeProposalStatus$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(
        ofType(ProposalActions.changeProposalStatus),
        switchMap(({ id, status }) => {


          return rs.changeProposalStatus(id, status).pipe(
            map((proposal) => ProposalActions.changeProposalStatusSuccess({ proposal })),
            catchError((error) =>
              of(ProposalActions.changeProposalStatusFailure({ error }))
            )
          );
        })
      );
    },
    { functional: true }
  );


  getProposalsAuditEntities$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(
        ofType(ProposalActions.getProposalsAuditEntities),
        switchMap(({ ids }) => {
          return rs.getProposalsAuditEntities(ids).pipe(
            map((auditEntities) => ProposalActions.getProposalsAuditEntitiesSuccess({
              auditEntities
            })),
            catchError((error) =>
              of(ProposalActions.getProposalsAuditEntitiesFailure({ error }))
            )
          );
        })
      );
    },
    { functional: true }
  );

  // uploadExcel$ = createEffect(
  //   (
  //     rs: ProposalService = inject(ProposalService),
  //     messageService: MessageService = inject(MessageService)
  //   ) => {
  //     return this.actions$.pipe(
  //       ofType(ProposalActions.uploadProposal),
  //       mergeMap(({ base64, ids, legalEntityIds, parameters, processCoverageIds, year, fields }) =>
  //         rs.uploadProposal(base64).pipe(
  //           mergeMap((res) => {
  //             // Gestione array di messaggi/errori
  //             if (res?.message && Array.isArray(res.message) && res.message.length > 0) {
  //               // Itera attraverso tutti i messaggi nell'array
  //               res.message.forEach((msg) => {
  //                 messageService.add({
  //                   summary: res?.type ?? 'Info',
  //                   detail: msg?.rowNumber && msg.rowNumber > 0 
  //                     ? `Row ${msg.rowNumber}: ${msg?.reason ?? 'No details available'}`
  //                     : msg?.reason ?? 'No details available',
  //                   severity: res?.status ? 'success' : 'error',
  //                   life: 10000
  //                 });
  //               });
  //             } else {
  //               // Fallback per messaggio singolo o assenza di messaggi
  //               messageService.add({
  //                 summary: res?.type ?? 'Info',
  //                 detail: 'Operation successfully completed',
  //                 severity: res?.status ? 'success' : 'error',
  //                 life: 10000
  //               });
  //             }

  //             // Array di actions da dispatchare
  //             return [
  //               ProposalActions.loadProposal({ ids, legalEntityIds, parameters, processCoverageIds, year, fields }),
  //               ProposalActions.loadProposalFilters({ legalEntityIds, proposalIds: ids, year })
  //             ];
  //           }),
  //           catchError((error) =>
  //             of(ProposalActions.uploadProposalFailure({ error }))
  //           )
  //         )
  //       )
  //     );
  //   },
  //   { functional: true }
  // );

  uploadExcel$ = createEffect(
    (
      rs: ProposalService = inject(ProposalService),
      messageService: MessageService = inject(MessageService)
    ) => {
      return this.actions$.pipe(
        ofType(ProposalActions.uploadProposal),
        mergeMap(({ base64, ids, legalEntityIds, parameters, processCoverageIds, year, fields }) =>
          rs.uploadProposal(base64).pipe(
            mergeMap((res) => {
              const actions: any[] = [];

              // Gestione errori
              if (res?.message && Array.isArray(res.message) && res.message.length > 0) {
                actions.push(ProposalActions.uploadProposalError({ errors: res.message as ImportRowErrorDto[] }));
              }

              // Gestione warnings
              if (res?.warnings && Array.isArray(res.warnings) && res.warnings.length > 0) {
                actions.push(ProposalActions.uploadProposalWarning({ warnings: res.warnings as ImportRowWarningDto[] }));
              }

              // Se non ci sono errori né warnings, mostra messaggio di successo
              if ((!res?.message || res.message.length === 0) && (!res?.warnings || res.warnings.length === 0)) {
                messageService.add({
                  summary: res?.type ?? 'Info',
                  detail: 'Operation successfully completed',
                  severity: res?.status ? 'success' : 'error',
                  life: 10000
                });
              }

              // Ricarica dati
              actions.push(
                ProposalActions.loadProposal({ ids, legalEntityIds, parameters, processCoverageIds, year, fields }),
                ProposalActions.loadProposalFilters({ legalEntityIds, proposalIds: ids, year })
              );

              return actions;
            }),
            catchError((error) =>
              of(ProposalActions.uploadProposalFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );


  exportProposalTemplate$ = createEffect(
    (usService: ProposalService = inject(ProposalService), as: AdministrationService = inject(AdministrationService)) => {
      return this.actions$.pipe(
        ofType(ProposalActions.downloadProposalTemplate),
        switchMap(() =>
          usService.downloadProposalTemplate().pipe(
            map((exportProposals) => {
              as.downloadFile(
                exportProposals!.content,
                exportProposals!.filename!
              );
              return ProposalActions.downloadProposalTemplateSuccess({
                file: exportProposals
              });
            }),
            catchError((error) =>
              of(ProposalActions.downloadProposalTemplateFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  getProposalsById$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(

        ofType(ProposalActions.getProposalsById),
        mergeMap(({ ids }) =>
          rs.getProposalsById(ids).pipe(
            map((proposals) => {
              return ProposalActions.getProposalsByIdSuccess({
                proposals
              });
            }),
            catchError((error) => of(ProposalActions.getProposalsByIdFailure({ error })))
          )
        )
      );
    },
    { functional: true }
  );

  getRecommendedAuditSubAreas$ = createEffect(
    (rs: ProposalService = inject(ProposalService)) => {
      return this.actions$.pipe(
        ofType(ProposalActions.getRecommendedAuditSubAreas),
        switchMap(({ proposalId, auditEntityIds }) =>
          rs.getRecommendedAuditSubAreas(auditEntityIds).pipe(
            map((recommendedSubAreas) =>
              ProposalActions.getRecommendedAuditSubAreasSuccess({ proposalId, recommendedSubAreas })
            ),
            catchError((error) =>
              of(ProposalActions.getRecommendedAuditSubAreasFailure({ error }))
            )
          )
        )
      );
    },
    { functional: true }
  );

  constructor(private actions$: Actions) { }
}
